import { useState, useEffect, useRef } from "react";
import { useLocation, useNavigate, Link } from "react-router-dom";
import {
  Menu, Search, Plus, Bell, LogOut, User, Shield, ChevronRight, CalendarClock,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useQuickAdd } from "@/context/QuickAddContext";
import { useAttention } from "@/context/AttentionContext";
import { NAV } from "@/components/Sidebar";
import SecurityDialog from "@/components/SecurityDialog";
import api from "@/lib/api";
import { toast } from "sonner";
import { initials } from "@/lib/format";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
  DropdownMenuLabel, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import {
  CommandDialog, CommandInput, CommandList, CommandItem, CommandEmpty, CommandGroup,
} from "@/components/ui/command";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";

const ROLE_LABELS = {
  tenant_owner: "Tenant Owner", platform_owner: "Platform Owner",
  sales_manager: "Sales Manager", sales_rep: "Sales Rep", readonly: "Read-Only",
};

function Breadcrumbs() {
  const { pathname } = useLocation();
  const seg = NAV.find((n) => (n.end ? pathname === n.to : pathname.startsWith(n.to) && n.to !== "/"));
  const label = pathname === "/" ? "Dashboard" : seg?.label || pathname.replace("/", "");
  return (
    <div className="hidden sm:flex items-center gap-1.5 text-sm" data-testid="breadcrumbs">
      <Link to="/" className="text-slate-400 hover:text-slate-600">Home</Link>
      <ChevronRight className="h-3.5 w-3.5 text-slate-300" />
      <span className="font-semibold text-slate-800">{label}</span>
    </div>
  );
}

export default function Topbar({ onMenu }) {
  const { user, tenant, logout, refresh: refreshMe } = useAuth();
  const { openQuickAdd } = useQuickAdd();
  const { counts, refresh } = useAttention();
  const [bookOpen, setBookOpen] = useState(false);
  const [bookUrl, setBookUrl] = useState("");
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [notes, setNotes] = useState({ items: [], unread: 0 });
  const [secOpen, setSecOpen] = useState(false);
  const t = useRef();
  const loadNotes = () => api.get("/notifications").then((r) => setNotes(r.data)).catch(() => {});

  useEffect(() => {
    const down = (e) => {
      if (e.key === "k" && (e.metaKey || e.ctrlKey)) { e.preventDefault(); setOpen((o) => !o); }
    };
    document.addEventListener("keydown", down);
    loadNotes();
    return () => document.removeEventListener("keydown", down);
  }, []);

  useEffect(() => {
    if (q.length < 2) { setResults([]); return; }
    clearTimeout(t.current);
    t.current = setTimeout(async () => {
      try { const { data } = await api.get(`/search?q=${encodeURIComponent(q)}`); setResults(data.results); } catch {}
    }, 250);
  }, [q]);

  const go = (r) => {
    setOpen(false); setQ("");
    if (r.type === "businesses") return navigate(`/businesses/${r.id}`);
    const route = { leads: "/leads", contacts: "/contacts", contracts: "/contracts", advertisers: "/advertisers" }[r.type];
    navigate(route || "/");
  };

  return (
    <header className="sticky top-0 z-30 flex h-16 items-center gap-3 border-b border-slate-200 bg-white/90 backdrop-blur-md px-4">
      <button onClick={onMenu} data-testid="topbar-menu-btn" className="lg:hidden h-9 w-9 flex items-center justify-center rounded-md hover:bg-slate-100">
        <Menu className="h-5 w-5" />
      </button>
      <Breadcrumbs />
      <div className="ml-auto flex items-center gap-2">
        <button
          onClick={() => setOpen(true)}
          data-testid="global-search-trigger"
          className="hidden md:flex items-center gap-2 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-400 hover:border-slate-300 w-64"
        >
          <Search className="h-4 w-4" />
          <span>Search…</span>
          <kbd className="ml-auto text-[10px] bg-white border rounded px-1.5 py-0.5">⌘K</kbd>
        </button>
        <button onClick={() => setOpen(true)} className="md:hidden h-9 w-9 flex items-center justify-center rounded-md hover:bg-slate-100">
          <Search className="h-5 w-5" />
        </button>

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button size="sm" data-testid="quick-new-btn" className="gap-1.5 rounded-lg">
              <Plus className="h-4 w-4" /> <span className="hidden sm:inline">New</span>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-52">
            <DropdownMenuItem onClick={() => openQuickAdd("lead")} data-testid="quick-new-lead">New Lead</DropdownMenuItem>
            <DropdownMenuItem onClick={() => openQuickAdd("business")} data-testid="quick-new-business">New Business</DropdownMenuItem>
            <DropdownMenuItem onClick={() => openQuickAdd("contact")} data-testid="quick-new-contact">New Contact</DropdownMenuItem>
            <DropdownMenuItem onClick={() => openQuickAdd("task")} data-testid="quick-new-task">New Task</DropdownMenuItem>
            <DropdownMenuItem onClick={() => openQuickAdd("meeting")} data-testid="quick-new-meeting">New Meeting</DropdownMenuItem>
            <DropdownMenuItem onClick={() => openQuickAdd("opportunity")} data-testid="quick-new-opportunity">New Opportunity</DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button data-testid="notifications-btn" className="relative h-9 w-9 flex items-center justify-center rounded-md hover:bg-slate-100">
              <Bell className="h-5 w-5 text-slate-600" />
              {(counts.notifications || 0) > 0 && (
                <span data-testid="bell-badge" className="absolute -top-0.5 -right-0.5 flex h-[17px] min-w-[17px] items-center justify-center rounded-full bg-[#FE3967] px-1 text-[10px] font-bold text-white tabular-nums leading-none">
                  {counts.notifications > 9 ? "9+" : counts.notifications}
                </span>
              )}
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-72">
            <DropdownMenuLabel>Notifications</DropdownMenuLabel>
            <DropdownMenuSeparator />
            {notes.items.length === 0 && <div className="px-3 py-6 text-center text-sm text-slate-400">You're all caught up</div>}
            {notes.items.slice(0, 8).map((n) => (
              <DropdownMenuItem key={n.id} data-testid={`notification-${n.id}`} className="flex-col items-start gap-0.5"
                onClick={async () => {
                  if (!n.read) { try { await api.post(`/notifications/${n.id}/read`); } catch { /* ignore */ } }
                  loadNotes(); refresh();
                  if (n.link) navigate(n.link);
                }}>
                <span className={"text-sm " + (!n.read ? "font-semibold text-slate-800" : "text-slate-600")}>{n.title}</span>
                {n.body && <span className="text-xs text-slate-400">{n.body}</span>}
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button data-testid="profile-menu-btn" className="flex items-center gap-2 rounded-lg hover:bg-slate-100 p-1 pr-2">
              <div className="h-8 w-8 rounded-full bg-[#00C9E0] text-white flex items-center justify-center text-xs font-bold">
                {initials(user?.name)}
              </div>
              <div className="hidden md:block text-left leading-tight">
                <div className="text-sm font-semibold text-slate-800">{user?.name}</div>
                <div className="text-[11px] text-slate-400">{ROLE_LABELS[user?.role]}</div>
              </div>
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56">
            <DropdownMenuLabel className="flex items-center gap-2">
              <Shield className="h-4 w-4 text-[#00C9E0]" /> {tenant?.name}
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => navigate("/settings")}><User className="h-4 w-4 mr-2" />Settings</DropdownMenuItem>
            <DropdownMenuItem onClick={() => setSecOpen(true)} data-testid="security-menu-item"><Shield className="h-4 w-4 mr-2" />Security</DropdownMenuItem>
            <DropdownMenuItem onClick={() => { setBookUrl(user?.booking_url || ""); setBookOpen(true); }} data-testid="scheduling-menu-item"><CalendarClock className="h-4 w-4 mr-2" />Scheduling Link</DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={logout} data-testid="logout-btn" className="text-[#C42607]"><LogOut className="h-4 w-4 mr-2" />Sign out</DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      <CommandDialog open={open} onOpenChange={setOpen} shouldFilter={false}>
        <CommandInput placeholder="Search businesses, leads, contacts…" value={q} onValueChange={setQ} data-testid="global-search-input" />
        <CommandList>
          <CommandEmpty>No results found.</CommandEmpty>
          {["businesses", "contacts", "leads", "advertisers", "contracts"].map((type) => {
            const group = results.filter((r) => r.type === type);
            if (!group.length) return null;
            return (
              <CommandGroup key={type} heading={type.charAt(0).toUpperCase() + type.slice(1)}>
                {group.map((r) => (
                  <CommandItem key={`${r.type}-${r.id}`} onSelect={() => go(r)} value={`${r.label} ${r.id}`}>
                    <span className="font-medium">{r.label}</span>
                    <span className="ml-auto text-xs text-slate-400">{r.sub}</span>
                  </CommandItem>
                ))}
              </CommandGroup>
            );
          })}
        </CommandList>
      </CommandDialog>
      <SecurityDialog open={secOpen} onOpenChange={setSecOpen} />

      <Dialog open={bookOpen} onOpenChange={setBookOpen}>
        <DialogContent data-testid="scheduling-dialog">
          <DialogHeader><DialogTitle className="font-display">My Scheduling Link</DialogTitle></DialogHeader>
          <div className="space-y-2 py-2">
            <p className="text-sm text-slate-500">Your personal booking URL (Microsoft Bookings, Outlook, Calendly, etc.). It powers the “Schedule a Call” link ({"{{booking_link}}"}) in your emails.</p>
            <Label className="text-xs">Scheduling Link (https://)</Label>
            <Input value={bookUrl} onChange={(e) => setBookUrl(e.target.value)} placeholder="https://outlook.office.com/bookwithme/..." data-testid="scheduling-input" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setBookOpen(false)}>Cancel</Button>
            <Button data-testid="scheduling-save" onClick={async () => {
              try { await api.put("/auth/me/booking-url", { booking_url: bookUrl }); await refreshMe(); toast.success("Scheduling Link saved"); setBookOpen(false); }
              catch (e) { toast.error(e.response?.data?.detail || "Could not save"); }
            }}>Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </header>
  );
}
